import { useState } from "react";
import { useTranslation } from "react-i18next";
import { apiFetchJson } from "../apiClient";
import usePagedList from "../hooks/usePagedList";

const EXPECT_TYPES = ["contains", "not_contains", "exact", "judge"];
const EMPTY_CASE = { input: "", type: "contains", value: "" };

/** Versions, publishing and evaluations of one agent (shown in the editor to its editors).
 * Editors run the draft; everyone else runs the published version. */
export default function AgentLifecyclePanel({ agent, onPublished = () => {} }) {
  const { t } = useTranslation();
  const base = `/api/v1/agents/${agent.id}`;
  const versions = usePagedList(`${base}/versions`);
  const datasets = usePagedList(`${base}/evals/datasets`);
  const runs = usePagedList(`${base}/evals/runs`);
  const [notes, setNotes] = useState("");
  const [name, setName] = useState("");
  const [cases, setCases] = useState([EMPTY_CASE]);
  const [openRun, setOpenRun] = useState(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState(null);

  const act = async (request, success, reload = []) => {
    setBusy(true);
    try {
      const result = await request();
      setMessage(success ? { tone: "success", text: success } : null);
      await Promise.all(reload.map((list) => list.reload()));
      return result;
    } catch (e) {
      setMessage({ tone: "danger", text: e.message });
      return null;
    } finally {
      setBusy(false);
    }
  };

  const publish = async (version) => {
    const updated = await act(() => apiFetchJson(`${base}/publish`, {
      method: "POST", body: JSON.stringify(version ? { version } : { notes }),
    }), t("lifecycle.published"), [versions]);
    if (updated) {
      setNotes("");
      onPublished(updated);
    }
  };

  const unpublish = async () => {
    const updated = await act(() => apiFetchJson(`${base}/unpublish`, { method: "POST" }), t("lifecycle.unpublished"),
                              [versions]);
    if (updated) onPublished(updated);
  };

  const createDataset = async (e) => {
    e.preventDefault();
    const body = { name, cases: cases.map((c) => ({ input: c.input, expect: { type: c.type, value: c.value } })) };
    const created = await act(() => apiFetchJson(`${base}/evals/datasets`, { method: "POST", body: JSON.stringify(body) }),
                              t("lifecycle.datasetCreated"), [datasets]);
    if (created) {
      setName("");
      setCases([EMPTY_CASE]);
    }
  };

  const setCase = (index, key) => (e) =>
    setCases((prev) => prev.map((c, i) => (i === index ? { ...c, [key]: e.target.value } : c)));

  const published = versions.items.find((v) => v.is_published);
  return (
    <section className="ds-card ac-form-stack" aria-label={t("lifecycle.title")}>
      <h3 className="ac-detail-heading">{t("lifecycle.title")}</h3>
      {message && <div className={`ds-alert ds-alert--${message.tone}`} role="alert">{message.text}</div>}

      <h4 className="ac-detail-heading">{t("lifecycle.versions")}</h4>
      <p className="ds-meta">
        {t("lifecycle.state", { draft: agent.version, published: published ? published.version : t("lifecycle.none") })}
      </p>
      <p className="ds-help">{t("lifecycle.help")}</p>
      {agent.can_manage && (
        <div className="ds-toolbar ac-toolbar-flush">
          <div className="ds-field ac-grow">
            <label className="ds-label" htmlFor="lifecycle-notes">{t("lifecycle.notes")}</label>
            <input id="lifecycle-notes" className="ds-input" value={notes} onChange={(e) => setNotes(e.target.value)} />
          </div>
          <button type="button" className="ds-btn ds-btn--primary" disabled={busy} onClick={() => publish(null)}>
            {t("lifecycle.publishDraft", { version: agent.version })}
          </button>
          {published && (
            <button type="button" className="ds-btn ds-btn--secondary" disabled={busy} onClick={unpublish}>
              {t("lifecycle.unpublish")}
            </button>
          )}
        </div>
      )}
      {versions.items.length > 0 && (
        <ul className="ds-list">
          {versions.items.map((v) => (
            <li key={v.version} className="ds-list-row ac-toolbar-flush">
              <span className="ac-grow">
                {t("agents.version", { version: v.version })} · {v.published_by} · {v.notes || "–"}
                {v.is_published && <span className="ds-badge ds-badge--success"> {t("lifecycle.live")}</span>}
              </span>
              {agent.can_manage && !v.is_published && (
                <button type="button" className="ds-btn ds-btn--secondary ds-btn--sm" disabled={busy}
                        onClick={() => publish(v.version)}>{t("lifecycle.rollback")}</button>
              )}
            </li>
          ))}
        </ul>
      )}

      <h4 className="ac-detail-heading">{t("lifecycle.evals")}</h4>
      <p className="ds-help">{t("lifecycle.evalsHelp")}</p>
      {datasets.items.length > 0 && (
        <ul className="ds-list">
          {datasets.items.map((d) => (
            <li key={d.id} className="ds-list-row ac-toolbar-flush">
              <span className="ac-grow">{d.name} · {t("lifecycle.caseCount", { count: d.cases.length })}</span>
              <button type="button" className="ds-btn ds-btn--secondary ds-btn--sm" disabled={busy}
                      onClick={() => act(() => apiFetchJson(`${base}/evals/datasets/${d.id}/runs`, { method: "POST" }),
                                         t("lifecycle.evalStarted"), [runs])}>
                {t("lifecycle.runEval")}
              </button>
            </li>
          ))}
        </ul>
      )}
      <form className="ac-form-stack" onSubmit={createDataset} aria-label={t("lifecycle.newDataset")}>
        <div className="ds-field">
          <label className="ds-label" htmlFor="lifecycle-dataset-name">{t("lifecycle.datasetName")}</label>
          <input id="lifecycle-dataset-name" className="ds-input" value={name} onChange={(e) => setName(e.target.value)} />
        </div>
        {cases.map((c, index) => (
          <div key={index} className="ds-toolbar ac-toolbar-flush" role="group"
               aria-label={t("lifecycle.caseLabel", { n: index + 1 })}>
            <input className="ds-input ac-grow" aria-label={t("lifecycle.caseInput")} placeholder={t("lifecycle.caseInput")}
                   value={c.input} onChange={setCase(index, "input")} />
            <select className="ds-select" aria-label={t("lifecycle.expectType")} value={c.type} onChange={setCase(index, "type")}>
              {EXPECT_TYPES.map((type) => <option key={type} value={type}>{t(`lifecycle.expect_${type}`)}</option>)}
            </select>
            <input className="ds-input ac-grow" aria-label={t("lifecycle.expectValue")} placeholder={t("lifecycle.expectValue")}
                   value={c.value} onChange={setCase(index, "value")} />
            {cases.length > 1 && (
              <button type="button" className="ds-btn ds-btn--secondary ds-btn--sm"
                      onClick={() => setCases((prev) => prev.filter((_, i) => i !== index))}>{t("lifecycle.removeCase")}</button>
            )}
          </div>
        ))}
        <div className="ac-actions">
          <button type="button" className="ds-btn ds-btn--secondary ds-btn--sm"
                  onClick={() => setCases((prev) => [...prev, EMPTY_CASE])}>{t("lifecycle.addCase")}</button>
          <button type="submit" className="ds-btn ds-btn--primary ds-btn--sm"
                  disabled={busy || !name.trim() || cases.some((c) => !c.input.trim() || !c.value.trim())}>
            {t("lifecycle.saveDataset")}
          </button>
        </div>
      </form>
      {runs.items.length > 0 && (
        <>
          <div className="ac-actions">
            <button type="button" className="ds-btn ds-btn--secondary ds-btn--sm" onClick={runs.reload}>
              {t("lifecycle.refresh")}
            </button>
          </div>
          <ul className="ds-list">
            {runs.items.map((r) => (
              <li key={r.id} className="ds-list-row">
                <button type="button" className="ds-list-toggle" aria-expanded={openRun === r.id}
                        onClick={() => setOpenRun(openRun === r.id ? null : r.id)}>
                  <span className="ds-badge">{t(`lifecycle.status_${r.status}`)}</span>
                  <span>{t("agents.version", { version: r.agent_version })}</span>
                  <span className="ds-meta">
                    {r.score === null ? "–" : t("lifecycle.score", { score: Math.round(r.score * 100), passed: r.passed, total: r.total })}
                  </span>
                </button>
                {openRun === r.id && (
                  <ul className="ds-list-detail">
                    {r.results.map((c, i) => (
                      <li key={i}>
                        {c.passed === null ? "…" : c.passed ? "✓" : "✗"} {c.input} — {c.detail}
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            ))}
          </ul>
        </>
      )}
    </section>
  );
}
